import { supabase } from "@/integrations/supabase/client";
import { v4 as uuidv4 } from 'uuid';

// Event types that can be tracked
export type TrackingEventName =
  | 'view_mode_picker'
  | 'form_started'
  | 'form_validated'
  | 'photo_uploaded'
  | 'photo_quality_failed'
  | 'report_generated'
  | 'email_sent_client'
  | 'lead_forwarded_frontdesk'
  | 'report_confirm_shown'
  | 'subscribe_plan'
  | 'error_send_failed'
  | 'utm_landing'
  | 'page_view';

interface TrackingEventData {
  event_name: TrackingEventName;
  metadata?: Record<string, any>;
  value?: number;
  currency?: string;
}

interface PixelConfig {
  enabled: boolean;
  use_gtm: boolean;
  gtm_container_id?: string;
  integrations: {
    meta?: { pixel_id: string; enabled: boolean };
    ga4?: { measurement_id: string; enabled: boolean };
    google_ads?: { conversion_id: string; enabled: boolean };
    tiktok?: { pixel_id: string; enabled: boolean };
    linkedin?: { partner_id: string; enabled: boolean };
  };
  privacy: {
    consent_required: boolean;
    hash_email: boolean;
    ip_anonymize: boolean;
  };
  utm_capture: boolean;
  debug_mode: boolean;
}

class TrackingClient {
  private config: PixelConfig | null = null;
  private tenantId: string | null = null;
  private brandId: string = 'default';
  private sessionId: string;
  private eventQueue: any[] = [];
  private utmParams: Record<string, string> = {};
  private firstReferrer: string = '';
  private consentGranted: { analytics: boolean; ads: boolean } = { analytics: false, ads: false };
  private initialized: boolean = false;
  private scriptsLoaded: { gtm: boolean; meta: boolean; ga4: boolean; tiktok: boolean; linkedin: boolean } = {
    gtm: false,
    meta: false,
    ga4: false,
    tiktok: false,
    linkedin: false
  };

  constructor() {
    this.sessionId = this.getOrCreateSessionId();
    this.firstReferrer = document.referrer;
    this.captureUTMParams();
  }

  async init(tenantId: string, brandId: string = 'default') {
    if (this.initialized) return;
    
    this.tenantId = tenantId;
    this.brandId = brandId;

    // Load pixel config
    const { data, error } = await supabase
      .from('pixel_configs')
      .select('*')
      .eq('tenant_id', tenantId)
      .eq('brand_id', brandId)
      .single();

    if (!error && data) {
      this.config = data as unknown as PixelConfig;
      
      if (this.config.enabled) {
        // Check for stored consent
        const stored = localStorage.getItem('tracking_consent');
        if (stored) {
          const consent = JSON.parse(stored);
          this.consentGranted = consent;
          if (consent.analytics || consent.ads) {
            this.initializePixels();
          }
        } else if (!this.config.privacy.consent_required) {
          // If consent not required, auto-grant
          this.consentGranted = { analytics: true, ads: true };
          this.initializePixels();
        }
      }
    }

    this.initialized = true;
  }

  private initializePixels() {
    if (!this.config) return;

    // Load GTM if configured
    if (this.config.use_gtm && this.config.gtm_container_id) {
      this.loadGTM(this.config.gtm_container_id);
      return;
    }

    // Load individual pixels
    if (this.config.integrations.meta?.enabled && this.config.integrations.meta.pixel_id) {
      this.loadMetaPixel(this.config.integrations.meta.pixel_id);
    }

    if (this.config.integrations.ga4?.enabled && this.config.integrations.ga4.measurement_id) {
      this.loadGA4(this.config.integrations.ga4.measurement_id);
    }

    if (this.config.integrations.tiktok?.enabled && this.config.integrations.tiktok.pixel_id) {
      this.loadTikTokPixel(this.config.integrations.tiktok.pixel_id);
    }

    if (this.config.integrations.linkedin?.enabled && this.config.integrations.linkedin.partner_id) {
      this.loadLinkedInInsight(this.config.integrations.linkedin.partner_id);
    }

    // Flush queued events
    this.flushQueue();
  }

  setConsent(analytics: boolean, ads: boolean) {
    this.consentGranted = { analytics, ads };
    localStorage.setItem('tracking_consent', JSON.stringify({ analytics, ads }));

    if (analytics || ads) {
      this.initializePixels();
      this.flushQueue();
    }

    // Save consent to database
    if (this.tenantId) {
      supabase.from('consent_records').insert({
        tenant_id: this.tenantId,
        session_id: this.sessionId,
        consent_analytics: analytics,
        consent_ads: ads,
        locale: navigator.language,
        user_agent: navigator.userAgent,
      });
    }
  }

  revokeConsent() {
    this.consentGranted = { analytics: false, ads: false };
    localStorage.removeItem('tracking_consent');
    this.eventQueue = [];

    if (this.tenantId) {
      supabase.from('consent_records')
        .update({ revoked_at: new Date().toISOString() })
        .eq('session_id', this.sessionId)
        .is('revoked_at', null);
    }
  }

  async track(eventData: TrackingEventData, userData?: { email?: string; phone?: string; country?: string }) {
    if (!this.config || !this.config.enabled || !this.tenantId) {
      console.log('Tracking not enabled or not initialized');
      return { status: 'skipped' };
    }

    const event = {
      event_id: uuidv4(),
      event_name: eventData.event_name,
      event_time: new Date().toISOString(),
      tenant_id: this.tenantId,
      brand_id: this.brandId,
      metadata: eventData.metadata || {},
      utm_data: this.utmParams,
      user_data: userData ? {
        email_hashed: userData.email && this.config.privacy.hash_email ? await this.sha256(userData.email.toLowerCase().trim()) : undefined,
        phone_hashed: userData.phone && this.config.privacy.hash_email ? await this.sha256(userData.phone.replace(/\D/g, '')) : undefined,
        country: userData.country,
      } : {},
      page: window.location.href,
      referrer: this.firstReferrer,
      device: {
        ua: navigator.userAgent,
        lang: navigator.language,
      },
      session_id: this.sessionId,
      currency: eventData.currency,
      value: eventData.value,
    };

    // Check consent
    const needsConsent = this.config.privacy.consent_required;
    const hasConsent = this.consentGranted.analytics || this.consentGranted.ads;

    if (needsConsent && !hasConsent) {
      // Queue for later
      this.eventQueue.push(event);
      console.log('Event queued (awaiting consent):', event.event_name);
      return { status: 'queued', event_id: event.event_id };
    }

    // Fire client-side events
    this.fireClientEvents(eventData.event_name, event);

    // Forward to server for CAPI
    try {
      const { data, error } = await supabase.functions.invoke('forward-tracking-event', {
        body: event
      });

      if (error) throw error;

      console.log('Event forwarded:', data);
      return { status: 'forwarded', event_id: event.event_id, ...data };
    } catch (error) {
      console.error('Failed to forward event:', error);
      return { status: 'error', event_id: event.event_id, error };
    }
  }

  private fireClientEvents(eventName: string, event: any) {
    if (!this.config) return;

    // Meta Pixel
    if (this.config.integrations.meta?.enabled && (window as any).fbq) {
      const metaEvent = this.mapEventToMeta(eventName);
      if (metaEvent) {
        (window as any).fbq('track', metaEvent, event.metadata);
      }
    }

    // GA4
    if (this.config.integrations.ga4?.enabled && (window as any).gtag) {
      const ga4Event = this.mapEventToGA4(eventName);
      if (ga4Event) {
        (window as any).gtag('event', ga4Event, event.metadata);
      }
    }

    // TikTok
    if (this.config.integrations.tiktok?.enabled && (window as any).ttq) {
      const tiktokEvent = this.mapEventToTikTok(eventName);
      if (tiktokEvent) {
        (window as any).ttq.track(tiktokEvent, event.metadata);
      }
    }
  }

  private mapEventToMeta(eventName: string): string | null {
    const mapping: Record<string, string> = {
      'form_validated': 'Lead',
      'report_confirm_shown': 'Lead',
      'subscribe_plan': 'CompleteRegistration',
    };
    return mapping[eventName] || null;
  }

  private mapEventToGA4(eventName: string): string | null {
    const mapping: Record<string, string> = {
      'form_validated': 'generate_lead',
      'report_generated': 'file_generate',
      'subscribe_plan': 'purchase',
    };
    return mapping[eventName] || null;
  }

  private mapEventToTikTok(eventName: string): string | null {
    const mapping: Record<string, string> = {
      'form_validated': 'SubmitForm',
      'report_confirm_shown': 'Contact',
    };
    return mapping[eventName] || null;
  }

  private flushQueue() {
    if (this.eventQueue.length === 0) return;

    console.log(`Flushing ${this.eventQueue.length} queued events`);
    const events = [...this.eventQueue];
    this.eventQueue = [];

    events.forEach(event => {
      this.fireClientEvents(event.event_name, event);
      supabase.functions.invoke('forward-tracking-event', { body: event });
    });
  }

  private getOrCreateSessionId(): string {
    let id = sessionStorage.getItem('tracking_session_id');
    if (!id) {
      id = uuidv4();
      sessionStorage.setItem('tracking_session_id', id);
    }
    return id;
  }

  private captureUTMParams() {
    const params = new URLSearchParams(window.location.search);
    const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
    
    utmKeys.forEach(key => {
      const value = params.get(key);
      if (value) {
        this.utmParams[key.replace('utm_', '')] = value;
      }
    });

    // Store for session
    if (Object.keys(this.utmParams).length > 0) {
      sessionStorage.setItem('utm_params', JSON.stringify(this.utmParams));
    } else {
      const stored = sessionStorage.getItem('utm_params');
      if (stored) {
        this.utmParams = JSON.parse(stored);
      }
    }
  }

  private async sha256(message: string): Promise<string> {
    const msgBuffer = new TextEncoder().encode(message);
    const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  private loadGTM(containerId: string) {
    if (this.scriptsLoaded.gtm) return;
    
    // Validate GTM container ID format (GTM-XXXXXXX)
    if (!/^GTM-[A-Z0-9]{7,}$/.test(containerId)) {
      console.error('Invalid GTM container ID format');
      return;
    }
    
    // Load GTM script using textContent instead of innerHTML for security
    const script = document.createElement('script');
    script.textContent = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${containerId}');
    `;
    document.head.appendChild(script);
    
    // Add noscript iframe
    const noscript = document.createElement('noscript');
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${containerId}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    noscript.appendChild(iframe);
    document.body.insertBefore(noscript, document.body.firstChild);
    
    this.scriptsLoaded.gtm = true;
  }

  private loadMetaPixel(pixelId: string) {
    if (this.scriptsLoaded.meta) return;

    // Validate Meta Pixel ID format (numeric only, 15-16 digits)
    if (!/^\d{15,16}$/.test(pixelId)) {
      console.error('Invalid Meta Pixel ID format');
      return;
    }

    const script = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '${pixelId}');
      fbq('track', 'PageView');
    `;

    const scriptEl = document.createElement('script');
    scriptEl.textContent = script;
    document.head.appendChild(scriptEl);
    this.scriptsLoaded.meta = true;
  }

  private loadGA4(measurementId: string) {
    if (this.scriptsLoaded.ga4) return;

    // Validate GA4 measurement ID format (G-XXXXXXXXXX)
    if (!/^G-[A-Z0-9]{10,}$/.test(measurementId)) {
      console.error('Invalid GA4 measurement ID format');
      return;
    }

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
    document.head.appendChild(script);

    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).gtag = function() { (window as any).dataLayer.push(arguments); };
    (window as any).gtag('js', new Date());
    (window as any).gtag('config', measurementId);
    this.scriptsLoaded.ga4 = true;
  }

  private loadTikTokPixel(pixelId: string) {
    if (this.scriptsLoaded.tiktok) return;

    // Validate TikTok Pixel ID format (alphanumeric, typically 20 chars)
    if (!/^[A-Z0-9]{15,25}$/.test(pixelId)) {
      console.error('Invalid TikTok Pixel ID format');
      return;
    }

    const script = `
      !function (w, d, t) {
        w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};
        ttq.load('${pixelId}');
        ttq.page();
      }(window, document, 'ttq');
    `;

    const scriptEl = document.createElement('script');
    scriptEl.textContent = script;
    document.head.appendChild(scriptEl);
    this.scriptsLoaded.tiktok = true;
  }

  private loadLinkedInInsight(partnerId: string) {
    if (this.scriptsLoaded.linkedin) return;

    // Validate LinkedIn Partner ID format (numeric, typically 6-8 digits)
    if (!/^\d{6,8}$/.test(partnerId)) {
      console.error('Invalid LinkedIn Partner ID format');
      return;
    }

    const script = `
      _linkedin_partner_id = "${partnerId}";
      window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
      window._linkedin_data_partner_ids.push(_linkedin_partner_id);
      (function(l) {
        if (!l){window.lintrk = function(a,b){window.lintrk.q.push([a,b])};
        window.lintrk.q=[]}
        var s = document.getElementsByTagName("script")[0];
        var b = document.createElement("script");
        b.type = "text/javascript";b.async = true;
        b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
        s.parentNode.insertBefore(b, s);})(window.lintrk);
    `;

    const scriptEl = document.createElement('script');
    scriptEl.textContent = script;
    document.head.appendChild(scriptEl);
    this.scriptsLoaded.linkedin = true;
  }
}

// Export singleton
export const trackingClient = new TrackingClient();
